/** @file This file contains helpers for maybe's. */
import { Maybe, NonEmptyList, Nothing, Right } from 'purify-ts';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function and(...val) {
    const res = [];
    for (const el of val) {
        if (el.isNothing()) {
            return Nothing;
        }
        res.push(el.extract());
    }
    return Maybe.of(res);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function any(arr) {
    const res = [];
    arr.forEach(el => {
        if (el.isJust()) {
            res.push(el.extract());
        }
    });
    return NonEmptyList.fromArray(res);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function swap(maybe, value) {
    return maybe.caseOf({
        Just: () => Maybe.empty(),
        Nothing: () => Maybe.of(value),
    });
}
/**
 * Returns the first element of an array of Maybes to be a Just. Returns Nothing if no element is
 * found.
 *
 * @param iterable An Iterable of Maybes.
 * @returns The first element of the array to be a Just if present. Nothing otherwise.
 */
export function first(iterable) {
    for (const el of iterable) {
        if (el.isNothing()) {
            continue;
        }
        return el;
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function fromBoolean(value) {
    if (value) {
        return Maybe.of(true);
    }
    return Maybe.empty();
}
/**
 * Wraps the passed data in a maybe as-is.
 *
 * @param data The data to wrap in a maybe.
 * @returns The maybe data.
 */
export function wrapMaybe(data) {
    if (Maybe.isMaybe(data)) {
        return data;
    }
    return Maybe.of(data);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function wrapMaybeNullable(value) {
    if (Maybe.isMaybe(value)) {
        return value;
    }
    return Maybe.fromNullable(value);
}
/**
 * Convert a nullable value to a `Maybe`.
 *
 * This is the same as {@link Maybe.fromNullable} but uses the `NonNullable` type which is useful with generic arguments.
 *
 * @param value The value to wrap in a Maybe.
 * @returns `value` in a `Just` if it is not `null` or `undefined`.
 */
export function fromNullable(value) {
    if (value == null) {
        return Maybe.empty();
    }
    else {
        return Maybe.of(value);
    }
}
/**
 * Covert a value to a {@link Maybe} of a specific type.
 *
 * @param typ The type `item` should have if the return value should be a just.
 * @param item The item to check for a given type.
 * @returns A {@link Just} of `item` if it is an instance of `typ`.
 */
export function asType(typ, item) {
    switch (arguments.length) {
        case 0:
            return asType;
        case 1:
            return asType.bind(undefined, typ);
        default:
            if (item instanceof typ) {
                return Maybe.of(item);
            }
            return Maybe.empty();
    }
}
/**
 * Convert a maybe to a left.
 *
 * This is very similar to {@link Maybe.toEither}, however a just will become left, not a right, and
 * there is no way to set the right value.
 *
 * @param val The value you want to convert to an Either.
 * @returns A left with the value of `val`, or an empty right otherwise.
 */
export function toLeft(val) {
    return val.toEither(undefined).swap();
}
/**
 * Convert an Maybe of an either to a Either of a maybe, mapping `Nothing` to `Right(Nothing)`.
 *
 * @param val The maybe either that will be converted to an either maybe.
 * @returns A Either of maybe a right.
 */
export function swapEither(val) {
    return val.caseOf({
        Nothing: () => Right(Nothing),
        Just: v => v.map(Maybe.of),
    });
}
/**
 * Check if two Maybes are considered equal.
 *
 * @param maybeA The first value to check.
 * @param maybeB The second value to check.
 * @param cmp The way the contents of the maybes should be compared.
 * @returns If the two values should be considered equal, which is only true when they are either
 * both `Nothing` or both just and `cmp` returns true.
 */
export function equals(maybeA, maybeB, cmp) {
    return maybeA.mapOrDefault(a => maybeB.mapOrDefault(b => cmp(a, b), false), maybeB.isNothing());
}
